import { Button, Card, Input, Row, Typography } from "antd";
import { message } from "@bindeleddet/core/utils";
import { useViewport } from "@bindeleddet/core/hooks";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookingSeason } from "../interface";
import { dryRunBooking } from "../bookingRequest";
import PriceReceipt from "../PriceReceipt/PriceReceipt";
import Calendar from "./Calendar";

const { Text, Title } = Typography;
const { TextArea } = Input;

const LEGEND: { label: string; style: React.CSSProperties }[] = [
  {
    label: "Valgt",
    style: { backgroundColor: "var(--primary-color)", border: "2px solid var(--secondary-color)" },
  },
  { label: "Ledig", style: { backgroundColor: "#c6ffc6" } },
  { label: "Opptatt", style: { backgroundColor: "#f97c7d" } },
  { label: "KarriereDagene", style: { backgroundColor: "#7a1e64" } },
  { label: "OsloTuren", style: { backgroundColor: "#1d3b67" } },
  { label: "Utilgjengelig", style: { backgroundColor: "#aeaaab" } },
];

export const DATE_TAKEN_MESSAGE = "Datoen ble nettopp booket av en annen bedrift. Velg en ny dato.";

const isSameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

interface Props {
  season: BookingSeason;
  // Fetches the booked days again, so the calendar shows dates booked by others since the page was loaded
  refreshBookedDays: () => void;
  setSelectedDate: (newDate: Date | undefined) => void;
  setDescription: (desc: string | undefined) => void;
  goToNextStep: () => void;
  selectedDate: Date | undefined;
  description: string | undefined;
}

const Booking: React.FC<Props> = ({
  season,
  refreshBookedDays,
  goToNextStep,
  selectedDate,
  setSelectedDate,
  setDescription,
  description,
}) => {
  const { width } = useViewport();
  const breakpoint = 1200;

  // Result of the dry-run for a date, e.g. an error if it has just been booked by someone else. The date is stored
  // so that the result of a previously selected date is never used for the current one.
  const [dateCheck, setDateCheck] = useState<{ date: Date; error?: string }>();

  // If the selected date shows up as booked when the calendar is refreshed, someone else got it first
  useEffect(() => {
    if (!selectedDate) return;
    if (season.booked_days.some(day => isSameDay(new Date(day), selectedDate))) {
      setSelectedDate(undefined);
      message("warning", DATE_TAKEN_MESSAGE);
    }
  }, [season.booked_days, selectedDate, setSelectedDate]);

  const buttonDisabled =
    selectedDate === undefined || dateCheck?.date !== selectedDate || dateCheck.error !== undefined;

  useEffect(
    function dryRun() {
      if (!selectedDate) return;
      // Do a dry-run of a booking to see if there is anything that prevents booking the selected date
      let cancelled = false;
      dryRunBooking(selectedDate).then(error => {
        if (cancelled) return;
        if (error?.code === "date_taken") {
          // The calendar was out of date: clear the selection and show the date as booked
          setSelectedDate(undefined);
          message("warning", DATE_TAKEN_MESSAGE);
          refreshBookedDays();
          return;
        }
        setDateCheck({ date: selectedDate, error: error?.message });
        if (error) message("error", error.message);
      });
      return () => {
        cancelled = true;
      };
    },
    [selectedDate, setSelectedDate, refreshBookedDays]
  );

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: width < breakpoint ? "1fr" : "1fr 1fr",
          gap: "16px",
          width: "100%",
        }}
      >
        <div>
          <Card
            style={{ width: "100%" }}
            bodyStyle={{ display: "flex", flexDirection: "column", alignItems: "center" }}
          >
            <Calendar
              bookingSeason={season}
              setSelectedDay={setSelectedDate}
              loading={false}
              initialSelected={selectedDate}
            />
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "6px 16px",
                width: "100%",
                marginTop: "16px",
              }}
            >
              {LEGEND.map(({ label, style }) => (
                <span key={label} style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      marginRight: "6px",
                      ...style,
                    }}
                  />
                  <Text style={{ fontSize: "12px" }}>{label}</Text>
                </span>
              ))}
            </div>
          </Card>
        </div>
        <div>
          <Card style={{ width: "100%", height: "100%" }}>
            <Title level={5}>Prisoversikt</Title>
            <PriceReceipt tiers={season.price_tiers} selectedDate={selectedDate} style={{ marginBottom: "24px" }} />
            <Title level={5}>Kommentar til Bindeleddet (valgfritt)</Title>
            <Text type="secondary" style={{ display: "block", marginBottom: "8px" }}>
              Har dere ønsker, spørsmål eller annet vi bør vite om arrangementet? Skriv det her, så tar vi det med oss.
            </Text>
            <TextArea
              value={description}
              // The backend accepts comments of at most 500 characters
              maxLength={500}
              showCount
              autoSize={{ minRows: 7, maxRows: 7 }}
              onChange={e => setDescription(e.target.value)}
              placeholder="F.eks. ønsket tidspunkt, antall deltakere, matpreferanser eller spørsmål til oss."
            />
          </Card>
        </div>
      </div>
      <Row style={{ justifyContent: "space-between", width: "100%", padding: "10px 0" }}>
        <Link to="/company/event-booking/">
          <Button type="default" size="large">
            Tilbake til informasjon
          </Button>
        </Link>
        <Button type="primary" size="large" disabled={buttonDisabled} onClick={goToNextStep}>
          Neste
        </Button>
      </Row>
    </>
  );
};

export default Booking;
